import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { formatNumber } from '@/lib/currency.utils';
import { formatPaymentDate } from './formatPaymentDate';
import {EquityDividendWithAccountsJoin} from "@/integrations/supabase/equity.models.ts";

export interface DividendHistoryTableProps {
  dividends: EquityDividendWithAccountsJoin[];
  isLoading: boolean;
  isError: boolean;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onRetry: () => void;
}

/**
 * Returns true when the dividend currency differs from the target account
 * currency, meaning a converted amount should be displayed.
 */
function hasCurrencyConversion(dividend: EquityDividendWithAccountsJoin): boolean {
  return !!dividend.accounts && dividend.currency !== dividend.accounts.currency;
}

export function DividendHistoryTable({
  dividends,
  isLoading,
  isError,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onRetry,
}: DividendHistoryTableProps) {
  const hasMoreRecords = totalCount > currentPage * pageSize;

  // Show converted amount column only when at least one row needs it.
  const anyConversion = dividends.some(hasCurrencyConversion);

  // Show notes column only when at least one row has a non-empty note.
  const anyNotes = dividends.some((d) => d.notes != null && d.notes.trim() !== '');

  if (isLoading) {
    return (
      <div
        className="space-y-2"
        data-testid="loading-state"
        aria-busy="true"
        aria-live="polite"
      >
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive" role="alert">
        <AlertCircle className="h-4 w-4" aria-hidden="true" />
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>Failed to load dividend history</span>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (dividends.length === 0) {
    return (
      <div
        className="flex items-center justify-center py-12 text-muted-foreground text-sm text-center px-4"
        data-testid="empty-state"
      >
        No dividends recorded yet
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto w-full">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Payment Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              {anyConversion && (
                <TableHead className="text-right">Converted Amount</TableHead>
              )}
              <TableHead>Account</TableHead>
              {anyNotes && <TableHead>Notes</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {dividends.map((dividend) => {
              const showConverted = hasCurrencyConversion(dividend);
              const hasNote = dividend.notes != null && dividend.notes.trim() !== '';

              return (
                <TableRow key={dividend.id}>
                  <TableCell>{formatPaymentDate(dividend.payment_date)}</TableCell>

                  <TableCell className="text-right">
                    {formatNumber(dividend.amount, 2)}{' '}
                    <span className="text-muted-foreground text-xs">
                      {dividend.currency}
                    </span>
                  </TableCell>

                  {anyConversion && (
                    <TableCell className="text-right">
                      {showConverted ? (
                        <>
                          {formatNumber(dividend.converted_amount, 2)}{' '}
                          <span className="text-muted-foreground text-xs">
                            {dividend.accounts?.currency}
                          </span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  )}

                  <TableCell>
                    {dividend.accounts?.name ?? (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>

                  {anyNotes && (
                    <TableCell>
                      {hasNote ? (
                        <span className="text-sm">{dividend.notes}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {hasMoreRecords && (
        <div className="flex justify-center pt-2">
          <Button
            variant="outline"
            onClick={() => onPageChange(currentPage + 1)}
            data-testid="load-more-button"
          >
            Load More
          </Button>
        </div>
      )}
    </div>
  );
}
